import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { SearchItemUnionProps, SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { usePopoverPosition } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { CompanyQuickActions } from '../company-quick-actions';
import { PeopleQuickActions } from '../people-quick-actions';
import * as Styled from './styles';
import * as SX from './sx';
import type { ActionButtonProps as Props } from './types';

export const ActionButton = memo(
  ({ className, item, searchType, fullWidth }: Props) => {
    const { setItem: setContextItem, item: contextItem } =
      useActionButtonContext();
    const ref = useRef<HTMLButtonElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const { anchorOrigin, transformOrigin } = usePopoverPosition(anchorEl, {
      thresholdPercentage: 25,
      anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
    });
    const open = Boolean(anchorEl && !!contextItem);

    // to delete previous anchorEl when contextItem is set to undefined in other places
    useEffect(() => {
      if (!contextItem) {
        setAnchorEl(null);
      }
    }, [contextItem]);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setContextItem(item as SearchItemUnionProps);
        setAnchorEl(event.currentTarget);
      },
      [setContextItem, item]
    );

    const handleClose = useCallback(() => {
      setContextItem(undefined);
      setAnchorEl(null);
    }, [setContextItem]);

    return (
      <>
        <Styled.Wrapper
          className={classnames('ActionsButton', className)}
          fullWidth={fullWidth}
          variant="outlined"
          onClick={handleClick}
          size="small"
          ref={ref}
          color="default"
          endIcon={
            <Icon name={open ? 'chevron-up' : 'chevron-down'} size={20} />
          }
        >
          Action
        </Styled.Wrapper>

        <Styled.Popover
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          onClose={handleClose}
          sx={SX.popover({
            marginBottom: anchorOrigin.vertical === 'top',
          })}
        >
          {searchType === SearchType.People ? (
            <PeopleQuickActions />
          ) : (
            <CompanyQuickActions />
          )}
        </Styled.Popover>
      </>
    );
  }
);

ActionButton.displayName = 'ActionsButton';
